import React, { useEffect, useState } from "react";
import { CreateCommanWay, GetCommanWay, ListCommanWay } from "API/commonApi";
import { useMutation, useQuery } from "@tanstack/react-query";
import moment from "moment";
import Swal from "sweetalert2";
import UseToast from "utils/AfToast";
import Select from "react-select";
import Spinner from "utils/Spinner";
import { AfDateRangePicker } from "components/Input/AfDateRangePicker";
import { Button } from "@heroui/react";
import { useNavigate } from "react-router-dom";

const IRN_BADGE = ({ status }: { status: string }) => {
  const cls =
    status === "ACTIVE"
      ? "bg-green-200 text-green-800"
      : status === "PENDING"
      ? "bg-yellow-200 text-yellow-800"
      : status === "CANCELLED"
      ? "bg-red-200 text-red-800"
      : "bg-gray-200 text-gray-600";
  return <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${cls}`}>{status || "N/A"}</span>;
};

const CnDnList: React.FC = () => {
  const navigate = useNavigate();
  const [tableData, setTableData] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const LIMIT = 25;

  // Filters
  const [noteType, setNoteType] = useState<any>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);
  const [selectedBranch, setSelectedBranch] = useState<any>(null);
  const [irnStatus, setIrnStatus] = useState<any>(null);
  const [startDate, setStartDate] = useState<any>(moment().startOf("month").format("YYYY-MM-DD"));
  const [endDate, setEndDate] = useState<any>(moment().endOf("day").format("YYYY-MM-DD"));

  // Options
  const [customerList, setCustomerList] = useState<any[]>([{ value: 0, label: "Loading..." }]);
  const [branchOptions, setBranchOptions] = useState<any[]>([{ value: 0, label: "Loading..." }]);

  const userD = (() => {
    const auth = localStorage.getItem("auth");
    return auth ? JSON.parse(auth) : {};
  })();
  const [userDetails] = useState<any>(userD?.state?.user || {});

  // Fetch customers
  useQuery(
    ["CustomersListApi_CnDn"],
    () => ListCommanWay("/admin/customer/list", { isActive: true }, { paginate: 1000 }),
    {
      keepPreviousData: true,
      onSuccess(data) {
        if (data.status == "SUCCESS") {
          let opt: any[] = [];
          data.data.data.forEach((p: any) => {
            opt.push({ value: Number(p.id), label: p.code + " - " + p.sh });
          });
          setCustomerList(opt);
        }
      },
    }
  );

  // Fetch branches
  useQuery(
    ["BranchesListApi_CnDn"],
    () => ListCommanWay("/admin/subbranch/list", {}, { paginate: 1000 }),
    {
      keepPreviousData: true,
      onSuccess(data) {
        if (data.status == "SUCCESS") {
          let opt: any[] = [];
          data.data.data.forEach((p: any) => {
            opt.push({ value: p.id, label: p.branch_name });
          });
          setBranchOptions(opt);
        }
      },
    }
  );

  // Build query object
  const buildQuery = () => {
    const q: any = {};
    if (noteType?.value) q.note_type = noteType.value;
    if (selectedCustomer?.value) q.customerId = selectedCustomer.value;
    if (selectedBranch?.value) q.subbranch = selectedBranch.value;
    if (irnStatus?.value) q.irn_status = irnStatus.value;
    if (startDate) q.startDate = startDate;
    if (endDate) q.endDate = endDate;
    return q;
  };

  // Fetch CN/DN list
  const { isFetching, isLoading, refetch } = useQuery(
    ["admin_cn_dn_list", page],
    () =>
      CreateCommanWay({
        url: "/admin/cn-dn/list",
        data: {
          query: buildQuery(),
          paginate: { page, limit: LIMIT },
        },
      }),
    {
      keepPreviousData: true,
      onSuccess(data: any) {
        if (data.status == "SUCCESS") {
          if (data.data?.docs) {
            setTableData(data.data.docs);
            setTotalPages(data.data.pages || 1);
            setTotal(data.data.total || 0);
          } else if (Array.isArray(data.data)) {
            setTableData(data.data);
            setTotalPages(1);
            setTotal(data.data.length);
          }
        } else {
          setTableData([]);
        }
      },
      onError: () => {
        setTableData([]);
      },
    }
  );

  const handleSearch = () => {
    setPage(1);
    refetch();
  };

  useEffect(() => {
    refetch();
  }, [page]);

  // Retry IRN
  const { mutate: mutateRetryIRN, isLoading: retryLoading } = useMutation(
    (id: number) =>
      CreateCommanWay({
        url: `/admin/cn-dn/${id}/retryIRN`,
        data: {},
      }),
    {
      onSuccess: (data: any) => {
        if (data.status == "SUCCESS") {
          UseToast("IRN activated successfully", "success");
          refetch();
        } else {
          UseToast(data.message || "Retry failed", "error");
        }
      },
      onError: (data: any) => {
        typeof data === "string" ? UseToast(data, "error") : UseToast(data.message, "error");
      },
    }
  );

  // Cancel IRN
  const { mutate: mutateCancelIRN, isLoading: cancelLoading } = useMutation(
    (params: { id: number; reason: string; remark: string }) =>
      CreateCommanWay({
        url: `/admin/cn-dn/${params.id}/cancel`,
        data: { reason: params.reason, remark: params.remark },
      }),
    {
      onSuccess: (data: any) => {
        if (data.status == "SUCCESS") {
          UseToast("IRN cancelled successfully", "success");
          refetch();
        } else {
          UseToast(data.message || "Cancel failed", "error");
        }
      },
      onError: (data: any) => {
        typeof data === "string" ? UseToast(data, "error") : UseToast(data.message, "error");
      },
    }
  );

  const handleCancel = (note: any) => {
    Swal.fire({
      title: "Cancel IRN",
      html: `
        <div style="text-align:left;">
          <p><strong>Note:</strong> ${note.note_no}</p>
          <label class="block mt-2 mb-1 font-semibold">Reason:</label>
          <select id="swal-reason" class="w-full border border-gray-300 rounded p-2">
            <option value="1">1 - Duplicate</option>
            <option value="2">2 - Data Entry Mistake</option>
          </select>
          <label class="block mt-2 mb-1 font-semibold">Remark (optional):</label>
          <input id="swal-remark" type="text" class="w-full border border-gray-300 rounded p-2" placeholder="Enter remark" />
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: "Cancel IRN",
      confirmButtonColor: "#dc2626",
      preConfirm: () => {
        const reason = (document.getElementById("swal-reason") as HTMLSelectElement)?.value;
        const remark = (document.getElementById("swal-remark") as HTMLInputElement)?.value;
        return { reason, remark };
      },
    }).then((result) => {
      if (result.isConfirmed && result.value) {
        mutateCancelIRN({ id: note.id, reason: result.value.reason, remark: result.value.remark });
      }
    });
  };

  return (
    <>
      {(isLoading || isFetching || retryLoading || cancelLoading) && <Spinner />}

      <h1 className="text-center my-2 text-lg font-semibold">Credit / Debit Notes</h1>

      {/* Filters */}
      <div className="px-4 border-[1px] p-2 rounded-md">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 justify-center">
          <div className="items-center gap-2">
            <label className="mb-2 text-sm font-semibold text-gray-900">Type</label>
            <Select
              className="w-36 bg-gray-50 border-gray-300 text-gray-900 text-sm rounded-lg block"
              options={[
                { value: "", label: "All" },
                { value: "CN", label: "Credit Note" },
                { value: "DN", label: "Debit Note" },
              ]}
              onChange={(e: any) => setNoteType(e)}
              value={noteType}
              isClearable={true}
            />
          </div>

          <div className="items-center gap-2">
            <label className="mb-2 text-sm font-semibold text-gray-900">Customer</label>
            <Select
              className="w-64 bg-gray-50 border-gray-300 text-gray-900 text-sm rounded-lg block"
              options={customerList}
              onChange={(e: any) => setSelectedCustomer(e)}
              value={selectedCustomer}
              isClearable={true}
            />
          </div>

          {(userDetails.role_name == 1 || userDetails.role_name == 8) && (
            <div className="items-center gap-2">
              <label className="mb-2 text-sm font-semibold text-gray-900">Subbranch</label>
              <Select
                className="w-48 bg-gray-50 border-gray-300 text-gray-900 text-sm rounded-lg block"
                options={branchOptions}
                onChange={(e: any) => setSelectedBranch(e)}
                value={selectedBranch}
                isClearable={true}
              />
            </div>
          )}

          <div className="items-center gap-2">
            <label className="mb-2 text-sm font-semibold text-gray-900">IRN Status</label>
            <Select
              className="w-40 bg-gray-50 border-gray-300 text-gray-900 text-sm rounded-lg block"
              options={[
                { value: "", label: "All" },
                { value: "ACTIVE", label: "Active" },
                { value: "PENDING", label: "Pending" },
                { value: "CANCELLED", label: "Cancelled" },
              ]}
              onChange={(e: any) => setIrnStatus(e)}
              value={irnStatus}
              isClearable={true}
            />
          </div>

          <AfDateRangePicker setStartDate={setStartDate} setEndDate={setEndDate} label="Date Range" />

          <Button type="button" className="ml-3 font-semibold font-inter text-white" radius="sm" color="success" onPress={handleSearch}>
            Search
          </Button>

          <Button
            type="button"
            className="font-semibold font-inter text-white"
            radius="sm"
            color="primary"
            onPress={() => navigate("/billing/cn-dn/create")}
          >
            + Create CN/DN
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="px-4 overflow-x-auto mt-2">
        <table className="w-full mb-4" style={{ fontSize: "85%" }}>
          <thead className="sticky top-0 bg-gray-300">
            <tr>
              <th className="border border-slate-200 px-2 py-1">Sr.</th>
              <th className="border border-slate-200 px-2 py-1">Note No</th>
              <th className="border border-slate-200 px-2 py-1">Type</th>
              <th className="border border-slate-200 px-2 py-1">Customer</th>
              <th className="border border-slate-200 px-2 py-1">Branch</th>
              <th className="border border-slate-200 px-2 py-1">Date</th>
              <th className="border border-slate-200 px-2 py-1">Reason</th>
              <th className="border border-slate-200 px-2 py-1 text-right">Base Value</th>
              <th className="border border-slate-200 px-2 py-1 text-right">Tax</th>
              <th className="border border-slate-200 px-2 py-1 text-right">Total</th>
              <th className="border border-slate-200 px-2 py-1">IRN Status</th>
              <th className="border border-slate-200 px-2 py-1">Actions</th>
            </tr>
          </thead>
          <tbody>
            {tableData.map((r: any, i: number) => (
              <tr key={"cn" + r.id} className="hover:bg-yellow-50">
                <td className="border border-slate-200 px-2 text-center">{(page - 1) * LIMIT + i + 1}</td>
                <td className="border border-slate-200 px-2 text-center font-mono font-semibold">{r.note_no}</td>
                <td className="border border-slate-200 px-2 text-center">
                  <span
                    className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                      r.note_type === "CN" ? "bg-blue-200 text-blue-800" : "bg-orange-200 text-orange-800"
                    }`}
                  >
                    {r.note_type}
                  </span>
                </td>
                <td className="border border-slate-200 px-2">{r._customerId?.code ? `${r._customerId.code} - ${r._customerId.sh}` : "Walk-in"}</td>
                <td className="border border-slate-200 px-2 text-center">{r._subbranch?.branch_name || r.subbranch}</td>
                <td className="border border-slate-200 px-2 text-center">{moment(r.note_date).format("DD/MM/YYYY")}</td>
                <td className="border border-slate-200 px-2">{r.reason}</td>
                <td className="border border-slate-200 px-2 pr-4 text-right">{Number(r.base_value || 0).toFixed(2)}</td>
                <td className="border border-slate-200 px-2 pr-4 text-right">{Number(r.tax || 0).toFixed(2)}</td>
                <td className="border border-slate-200 px-2 pr-4 text-right font-semibold">{Number(r.total_value || 0).toFixed(2)}</td>
                <td className="border border-slate-200 px-2 text-center">
                  <IRN_BADGE status={r.irn_status} />
                </td>
                <td className="border border-slate-200 px-2 text-center whitespace-nowrap">
                  <button
                    onClick={() => navigate(`/billing/cn-dn/${r.id}`)}
                    className="px-2 py-0.5 bg-blue-600 text-white rounded-sm text-xs mr-1"
                  >
                    View
                  </button>
                  {r.irn_status === "PENDING" && (
                    <button
                      onClick={() => mutateRetryIRN(r.id)}
                      className="px-2 py-0.5 bg-yellow-600 text-white rounded-sm text-xs mr-1"
                    >
                      Retry IRN
                    </button>
                  )}
                  {r.irn_status === "ACTIVE" && (
                    <button onClick={() => handleCancel(r)} className="px-2 py-0.5 bg-red-600 text-white rounded-sm text-xs">
                      Cancel
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {tableData.length === 0 && (
              <tr>
                <td colSpan={12} className="text-center py-4 text-gray-500">
                  No Credit/Debit Notes found.
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mb-4">
            <button
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
              className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50"
            >
              Previous
            </button>
            <span className="text-sm">
              Page {page} of {totalPages} ({total} total)
            </span>
            <button
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
              className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default CnDnList;
